import React from "react";
import styled from "styled-components";
import { palette } from "../../styles/palette";
import { typography } from "../../styles/typography";

interface ConfirmPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string; // 질문 텍스트
    highlightText?: string; // 강조할 텍스트 (예: "삭제")
    hideCancel?: boolean; // 취소 버튼 숨김 옵션
}

const ConfirmPopup: React.FC<ConfirmPopupProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    highlightText,
    hideCancel = false
}) => {
    if (!isOpen) return null;

    // 강조 텍스트가 있으면 타이틀에서 해당 부분만 강조하여 표시
    const renderTitle = () => {
        if (!highlightText) return title;

        const parts = title.split(highlightText);
        return (
            <>
                {parts[0]}
                <HighlightSpan>{highlightText}</HighlightSpan>
                {parts[1]}
            </>
        );
    };

    return (
        <Backdrop onClick={onClose}>
            <PopupContainer onClick={(e) => e.stopPropagation()}>
                <IconContainer>
                    <AlertIcon>!</AlertIcon>
                </IconContainer>

                <TitleContainer>{renderTitle()}</TitleContainer>

                <ButtonContainer hideCancel={hideCancel}>
                    {!hideCancel && <CancelButton onClick={onClose}>취소</CancelButton>}
                    <ConfirmButton onClick={onConfirm} fullWidth={hideCancel}>
                        확인
                    </ConfirmButton>
                </ButtonContainer>
            </PopupContainer>
        </Backdrop>
    );
};

const Backdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const PopupContainer = styled.div`
    width: 90%;
    max-width: 350px;
    background-color: ${palette.white};
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 16px;
`;

const IconContainer = styled.div`
    margin-bottom: 16px;
`;

const AlertIcon = styled.div`
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: ${palette.gray[100]};
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 32px;
    font-weight: bold;
    color: ${palette.white};
`;

const TitleContainer = styled.div`
    ${typography.title3}
    color: ${palette.gray[900]};
    text-align: center;
    margin-bottom: 24px;
    word-break: keep-all;
    line-height: 1.5;
`;

const HighlightSpan = styled.span`
    color: ${palette.primary};
    font-weight: bold;
`;

const ButtonContainer = styled.div<{ hideCancel?: boolean }>`
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
`;

const Button = styled.button`
    flex: 1;
    padding: 12px 0;
    border-radius: 8px;
    border: none;
    ${typography.button}
    cursor: pointer;
`;

const CancelButton = styled(Button)`
    background-color: ${palette.gray[200]};
    color: ${palette.gray[800]};
`;

const ConfirmButton = styled(Button)<{ fullWidth?: boolean }>`
    background-color: ${palette.primary};
    color: ${palette.white};
    width: ${(props) => (props.fullWidth ? "100%" : "auto")};
`;

export default ConfirmPopup;
